<script setup lang="ts">
import * as R from 'ramda'
import { ref, useTemplateRef, watch } from 'vue'

import { MultiSelect, SuccessBadge } from '@collector/shared-ui'
import { ValueType } from '@collector/shared-utils'
import { Incident } from '@collector/sportsapi-client'
import { useSportMaps } from '@desktop/views/Relation/composables'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'
import { IncidentSettings } from '@desktop/views/Relation/Sports/configuration'
import { injectAttributesToResetParticipants } from '@desktop/views/Relation/Sports/provide-inject'

import { shouldResetParticipants } from './utils'

interface Props {
  attributesSelectionConstraint?: NonNullable<
    IncidentSettings['attributesSelectionConstraint']
  >
  incident: Incident
  disabled: boolean
  attributeIds: NonNullable<ValueType<IncidentSettings['attributeIds']>>
  singleAttributes: NonNullable<IncidentSettings['singleAttributes']>
}

const props = defineProps<Props>()

// inject
const attributesToResetParticipants = injectAttributesToResetParticipants()

// composables
const { incidentAttributeIdToAttributeMap } = useSportMaps()
const eventIncidentsQueue = useEventIncidentsQueue()

// data
const attributesOptions = props.attributeIds
  .filter((id) => incidentAttributeIdToAttributeMap.value[id]?.name)
  .map((id) => ({
    name: incidentAttributeIdToAttributeMap.value[id]?.name,
    value: id,
  }))

// state
const selectedAttributeIds = ref<number[] | number | undefined>(
  getSelectedAttributeIdsValue(),
)
const successBadge = useTemplateRef('successBadge')

// methods
function getSelectedAttributeIdsValue(): number[] | number | undefined {
  if (props.singleAttributes) {
    return props.incident.attribute_ids === null ||
      props.incident.attribute_ids.length === 0
      ? undefined
      : props.incident.attribute_ids[0]
  }

  return props.incident.attribute_ids === null
    ? []
    : R.clone(props.incident.attribute_ids)
}

function updateAttributes(): void {
  const selectedAttributeIdsArray = Array.isArray(selectedAttributeIds.value)
    ? selectedAttributeIds.value
    : selectedAttributeIds.value !== undefined
      ? [selectedAttributeIds.value]
      : []

  // prevent updating:
  // - no changes in attributes
  // - same attributes clicked in different order
  if (
    R.symmetricDifference(
      props.incident.attribute_ids || [],
      selectedAttributeIdsArray,
    ).length === 0
  ) {
    return
  }

  if (
    shouldResetParticipants(
      props.incident.attribute_ids,
      selectedAttributeIdsArray,
      attributesToResetParticipants,
    )
  ) {
    eventIncidentsQueue.updateIncident(
      {
        attribute_ids: [...selectedAttributeIdsArray],
        participant_id: null,
        assistant_id: null,
        participant_name: null,
        assistant_name: null,
      },
      props.incident,
    )
    return
  }

  eventIncidentsQueue.updateIncident(
    { attribute_ids: [...selectedAttributeIdsArray] },
    props.incident,
  )
}

// init
watch(
  () => props.incident.attribute_ids,
  () => {
    const attributeIds = getSelectedAttributeIdsValue()

    if (!R.equals(attributeIds, selectedAttributeIds.value)) {
      selectedAttributeIds.value = attributeIds
    } else {
      successBadge.value?.show()
    }
  },
)
</script>

<template>
  <MultiSelect
    v-model="selectedAttributeIds"
    class="gap-x-2"
    :options="attributesOptions"
    :selectionConstraint="attributesSelectionConstraint"
    labelClass="min-w-13 max-w-13 justify-end"
    :disabled
    withClear
    withFilter
    withOptionsFullWidth
    data-testid="attributes-select"
    @blur="updateAttributes"
    @clearValue="updateAttributes"
  >
    <template #default>attributes</template>
    <template #trigger>
      <SuccessBadge
        ref="successBadge"
        title="attributes updated"
      />
    </template>
  </MultiSelect>
</template>
